/**
 * Centralized date utility functions using date-fns and date-fns-tz
 * Provides consistent, timezone-aware date operations across the application
 * Replaces moment.js with modern date-fns library
 */

import {
  format,
  parse,
  startOfWeek,
  endOfWeek,
  startOfMonth,
  endOfMonth,
  startOfYear,
  endOfYear,
  addDays,
  subDays,
  subWeeks,
  subMonths,
  subYears,
  differenceInSeconds,
  isSameDay,
  isSameWeek,
  isSameMonth,
  isToday as isTodayFn
} from 'date-fns';
import { formatInTimeZone, toZonedTime, fromZonedTime } from 'date-fns-tz';

// ==================== DATE FORMATTING ====================

/**
 * Format a date for display with weekday and date
 * @param {Date|string} date - The date to format
 * @param {string} timezone - IANA timezone string (optional, uses local if not provided)
 * @returns {string} Formatted date (e.g., "Mon, Jan 15")
 */
export const formatWorklogDate = (date, timezone = null) => {
  if (!date) return '';
  const dateObj = typeof date === 'string' ? new Date(date) : date;

  if (timezone) {
    return formatInTimeZone(dateObj, timezone, 'EEE, MMM d');
  }
  return format(dateObj, 'EEE, MMM d');
};

/**
 * Format a date range for display
 * @param {Date|string} from - Start date
 * @param {Date|string} to - End date
 * @param {string} timezone - IANA timezone string (optional)
 * @returns {string} Formatted date range
 */
export const formatDateRange = (from, to, timezone = null) => {
  if (!from || !to) return '';

  const fromDate = typeof from === 'string' ? new Date(from) : from;
  const toDate = typeof to === 'string' ? new Date(to) : to;

  const formatDate = timezone
    ? (d, fmt) => formatInTimeZone(d, timezone, fmt)
    : (d, fmt) => format(d, fmt);

  if (isSameDay(fromDate, toDate)) {
    return formatDate(fromDate, 'MMM d, yyyy');
  }

  if (fromDate.getFullYear() === toDate.getFullYear()) {
    return `${formatDate(fromDate, 'MMM d')} - ${formatDate(toDate, 'MMM d, yyyy')}`;
  }

  return `${formatDate(fromDate, 'MMM d, yyyy')} - ${formatDate(toDate, 'MMM d, yyyy')}`;
};

/**
 * Format date for backend API (YYYY-MM-DD)
 * @param {Date|string} date - The date to format
 * @returns {string} Date in YYYY-MM-DD format
 */
export const formatDateForBackend = (date) => {
  if (!date) return '';
  const dateObj = typeof date === 'string' ? new Date(date) : date;
  return format(dateObj, 'yyyy-MM-dd');
};

/**
 * Format date for display in tables and lists
 * @param {Date|string} date - The date to format
 * @param {string} timezone - IANA timezone string (optional)
 * @returns {string} Formatted date (e.g., "Jan 15, 2025")
 */
export const formatDisplayDate = (date, timezone = null) => {
  if (!date) return '';
  const dateObj = typeof date === 'string' ? new Date(date) : date;

  if (timezone) {
    return formatInTimeZone(dateObj, timezone, 'MMM d, yyyy');
  }
  return format(dateObj, 'MMM d, yyyy');
};

/**
 * Format date for detailed display with weekday
 * @param {Date|string} date - The date to format
 * @param {string} timezone - IANA timezone string (optional)
 * @returns {string} Formatted date (e.g., "Monday, January 15, 2025")
 */
export const formatLongDate = (date, timezone = null) => {
  if (!date) return '';
  const dateObj = typeof date === 'string' ? new Date(date) : date;

  if (timezone) {
    return formatInTimeZone(dateObj, timezone, 'EEEE, MMMM d, yyyy');
  }
  return format(dateObj, 'EEEE, MMMM d, yyyy');
};

/**
 * Format date for calendar headers
 * @param {Date|string} date - The date to format
 * @param {string} timezone - IANA timezone string (optional)
 * @returns {string} Formatted date (e.g., "January 2025")
 */
export const formatMonthYear = (date, timezone = null) => {
  if (!date) return '';
  const dateObj = typeof date === 'string' ? new Date(date) : date;

  if (timezone) {
    return formatInTimeZone(dateObj, timezone, 'MMMM yyyy');
  }
  return format(dateObj, 'MMMM yyyy');
};

// ==================== TIME FORMATTING ====================

/**
 * Format time for display (12-hour format)
 * @param {Date|string} time - The time to format
 * @param {string} timezone - IANA timezone string (optional)
 * @returns {string} Formatted time (e.g., "2:30 PM")
 */
export const formatTime12Hour = (time, timezone = null) => {
  if (!time) return '';
  const timeObj = typeof time === 'string' ? new Date(time) : time;

  if (timezone) {
    return formatInTimeZone(timeObj, timezone, 'h:mm a');
  }
  return format(timeObj, 'h:mm a');
};

/**
 * Format hour and minute to 12-hour format
 * @param {number} hour - Hour (0-23)
 * @param {number} minute - Minute (0-59)
 * @param {string} timezone - IANA timezone string (optional)
 * @returns {string} Formatted time (e.g., "2:30 PM")
 */
export const formatHourMinute12Hour = (hour, minute, timezone = null) => {
  const date = new Date();
  date.setHours(hour, minute, 0, 0);

  if (timezone) {
    return formatInTimeZone(date, timezone, 'h:mm a');
  }
  return format(date, 'h:mm a');
};

/**
 * Format time for display (24-hour format)
 * @param {Date|string} time - The time to format
 * @param {string} timezone - IANA timezone string (optional)
 * @returns {string} Formatted time (e.g., "14:30")
 */
export const formatTime24Hour = (time, timezone = null) => {
  if (!time) return '';
  const timeObj = typeof time === 'string' ? new Date(time) : time;

  if (timezone) {
    return formatInTimeZone(timeObj, timezone, 'HH:mm');
  }
  return format(timeObj, 'HH:mm');
};

/**
 * Format time range for display
 * @param {Date|string} startTime - Start time
 * @param {Date|string} endTime - End time
 * @param {string} timezone - IANA timezone string (optional)
 * @returns {string} Formatted time range (e.g., "2:30 PM - 5:00 PM")
 */
export const formatTimeRange = (startTime, endTime, timezone = null) => {
  if (!startTime || !endTime) return '';

  const start = typeof startTime === 'string' ? new Date(startTime) : startTime;
  const end = typeof endTime === 'string' ? new Date(endTime) : endTime;

  const formatTime = timezone
    ? (t) => formatInTimeZone(t, timezone, 'h:mm a')
    : (t) => format(t, 'h:mm a');

  return `${formatTime(start)} - ${formatTime(end)}`;
};

// ==================== DURATION FORMATTING ====================

/**
 * Format duration in seconds to HH:MM:SS
 * @param {number} seconds - Duration in seconds
 * @returns {string} Formatted duration (e.g., "2:30:45")
 */
export const formatDuration = (seconds) => {
  if (!seconds || seconds === 0) return '00:00:00';

  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  const secs = seconds % 60;

  return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
};

/**
 * Format duration in seconds to human readable format
 * @param {number} seconds - Duration in seconds
 * @returns {string} Human readable duration (e.g., "2h 30m")
 */
export const formatDurationHuman = (seconds) => {
  if (!seconds || seconds === 0) return '0m';

  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);

  if (hours === 0) return `${minutes}m`;
  if (minutes === 0) return `${hours}h`;
  return `${hours}h ${minutes}m`;
};

// ==================== DATE CALCULATIONS ====================

/**
 * Get the start of the week (Monday) for a given date in specified timezone
 * @param {Date|string} date - The date
 * @param {string} timezone - IANA timezone string (optional)
 * @returns {Date} Monday of the week
 */
export const getWeekStart = (date, timezone = null) => {
  const dateObj = typeof date === 'string' ? new Date(date) : date;

  if (timezone) {
    const zonedDate = toZonedTime(dateObj, timezone);
    const weekStart = startOfWeek(zonedDate, { weekStartsOn: 1 }); // Monday
    return fromZonedTime(weekStart, timezone);
  }

  return startOfWeek(dateObj, { weekStartsOn: 1 }); // Monday
};

/**
 * Get the end of the week (Sunday) for a given date in specified timezone
 * @param {Date|string} date - The date
 * @param {string} timezone - IANA timezone string (optional)
 * @returns {Date} Sunday of the week
 */
export const getWeekEnd = (date, timezone = null) => {
  const dateObj = typeof date === 'string' ? new Date(date) : date;

  if (timezone) {
    const zonedDate = toZonedTime(dateObj, timezone);
    const weekEnd = endOfWeek(zonedDate, { weekStartsOn: 1 }); // Sunday
    return fromZonedTime(weekEnd, timezone);
  }

  return endOfWeek(dateObj, { weekStartsOn: 1 }); // Sunday
};

/**
 * Get an array of dates for a week starting from Monday
 * @param {Date|string} date - Any date in the week
 * @param {boolean} includeWeekends - Whether to include Saturday and Sunday
 * @param {string} timezone - IANA timezone string (optional)
 * @returns {Date[]} Array of dates in the week
 */
export const getWeekDates = (date, includeWeekends = true, timezone = null) => {
  const weekStart = getWeekStart(date, timezone);
  const daysToShow = includeWeekends ? 7 : 5;
  const dates = [];

  for (let i = 0; i < daysToShow; i++) {
    dates.push(addDays(weekStart, i));
  }

  return dates;
};

/**
 * Format week range for display
 * @param {Date|string} date - Any date in the week
 * @param {string} timezone - IANA timezone string (optional)
 * @returns {string} Formatted week range (e.g., "Jul 14 - Jul 20")
 */
export const formatWeekRange = (date, timezone = null) => {
  const weekStart = getWeekStart(date, timezone);
  const weekEnd = getWeekEnd(date, timezone);

  const formatDate = timezone
    ? (d, fmt) => formatInTimeZone(d, timezone, fmt)
    : (d, fmt) => format(d, fmt);

  if (weekStart.getMonth() === weekEnd.getMonth()) {
    return `${formatDate(weekStart, 'MMM d')} - ${formatDate(weekEnd, 'd')}`;
  }

  return `${formatDate(weekStart, 'MMM d')} - ${formatDate(weekEnd, 'MMM d')}`;
};

/**
 * Get date range for preset options (Today, Yesterday, This week, etc.)
 * @param {string} preset - The preset option
 * @param {string} timezone - IANA timezone string (optional)
 * @returns {Object} Object with 'from' and 'to' date strings (YYYY-MM-DD)
 */
export const getDateRangeForPreset = (preset, timezone = null) => {
  const today = new Date();

  switch (preset) {
    case 'Today':
      return {
        from: formatDateForBackend(today),
        to: formatDateForBackend(today)
      };

    case 'Yesterday':
      const yesterday = subDays(today, 1);
      return {
        from: formatDateForBackend(yesterday),
        to: formatDateForBackend(yesterday)
      };

    case 'This week':
      return {
        from: formatDateForBackend(getWeekStart(today, timezone)),
        to: formatDateForBackend(getWeekEnd(today, timezone))
      };

    case 'Last week':
      const lastWeekStart = subWeeks(getWeekStart(today, timezone), 1);
      const lastWeekEnd = endOfWeek(lastWeekStart, { weekStartsOn: 1 });
      return {
        from: formatDateForBackend(lastWeekStart),
        to: formatDateForBackend(lastWeekEnd)
      };

    case 'Past two weeks':
      const twoWeeksAgo = subWeeks(getWeekStart(today, timezone), 2);
      return {
        from: formatDateForBackend(twoWeeksAgo),
        to: formatDateForBackend(getWeekEnd(today, timezone))
      };

    case 'This month':
      return {
        from: formatDateForBackend(startOfMonth(today)),
        to: formatDateForBackend(endOfMonth(today))
      };

    case 'Last month':
      const lastMonth = subMonths(today, 1);
      return {
        from: formatDateForBackend(startOfMonth(lastMonth)),
        to: formatDateForBackend(endOfMonth(lastMonth))
      };

    case 'This year':
      return {
        from: formatDateForBackend(startOfYear(today)),
        to: formatDateForBackend(endOfYear(today))
      };

    case 'Last year':
      const lastYear = subYears(today, 1);
      return {
        from: formatDateForBackend(startOfYear(lastYear)),
        to: formatDateForBackend(endOfYear(lastYear))
      };

    default:
      return { from: '', to: '' };
  }
};

// ==================== DATE VALIDATION & COMPARISON ====================

/**
 * Check if a worklog spans overnight (end time is on the next day)
 * @param {Object} worklog - Worklog object with startTime and endTime
 * @returns {boolean} True if worklog spans overnight
 */
export const isOvernightWorklog = (worklog) => {
  if (!worklog.startTime || !worklog.endTime) return false;

  const startTime = typeof worklog.startTime === 'string' ? new Date(worklog.startTime) : worklog.startTime;
  const endTime = typeof worklog.endTime === 'string' ? new Date(worklog.endTime) : worklog.endTime;

  return !isSameDay(startTime, endTime);
};

/**
 * Check if a date is today
 * @param {Date|string} date - The date to check
 * @returns {boolean} True if date is today
 */
export const isToday = (date) => {
  if (!date) return false;
  const dateObj = typeof date === 'string' ? new Date(date) : date;
  return isTodayFn(dateObj);
};

/**
 * Check if a date is in the current week
 * @param {Date|string} date - The date to check
 * @returns {boolean} True if date is in current week
 */
export const isThisWeek = (date) => {
  if (!date) return false;
  const dateObj = typeof date === 'string' ? new Date(date) : date;
  return isSameWeek(dateObj, new Date(), { weekStartsOn: 1 });
};

/**
 * Check if a date is in the current month
 * @param {Date|string} date - The date to check
 * @returns {boolean} True if date is in current month
 */
export const isThisMonth = (date) => {
  if (!date) return false;
  const dateObj = typeof date === 'string' ? new Date(date) : date;
  return isSameMonth(dateObj, new Date());
};

// ==================== CONVERSION UTILITIES ====================

/**
 * Convert time string to Date object for a specific date in timezone
 * @param {string} timeString - Time in HH:MM format
 * @param {Date|string} date - The date to apply the time to
 * @param {string} timezone - IANA timezone string (optional)
 * @returns {Date} Date object with the specified time
 */
export const timeStringToDate = (timeString, date = new Date(), timezone = null) => {
  const [hours, minutes] = timeString.split(':').map(Number);
  const dateObj = typeof date === 'string' ? new Date(date) : new Date(date);

  if (timezone) {
    // Create date in the specified timezone
    const zonedDate = toZonedTime(dateObj, timezone);
    zonedDate.setHours(hours, minutes, 0, 0);
    return fromZonedTime(zonedDate, timezone);
  }

  dateObj.setHours(hours, minutes, 0, 0);
  return dateObj;
};

/**
 * Create a Date object from date string and time string in a specific timezone
 * @param {string} dateStr - Date string in YYYY-MM-DD format
 * @param {string} timeStr - Time string in HH:MM format
 * @param {string} timezone - IANA timezone string
 * @returns {Date} UTC Date object representing the time in the given timezone
 */
export const createDateTimeInTimezone = (dateStr, timeStr, timezone) => {
  if (!dateStr || !timeStr) return null;

  const dateTimeStr = `${dateStr} ${timeStr}:00`;
  const parsedDate = parse(dateTimeStr, 'yyyy-MM-dd HH:mm:ss', new Date());

  if (timezone) {
    return fromZonedTime(parsedDate, timezone);
  }

  return parsedDate;
};

/**
 * Get current time formatted for display
 * @param {string} timezone - IANA timezone string (optional)
 * @returns {string} Current time in 12-hour format with seconds
 */
export const getCurrentTimeFormatted = (timezone = null) => {
  const now = new Date();

  if (timezone) {
    return formatInTimeZone(now, timezone, 'h:mm:ss a');
  }
  return format(now, 'h:mm:ss a');
};

/**
 * Calculate duration between two times in seconds
 * @param {Date|string} startTime - Start time
 * @param {Date|string} endTime - End time
 * @returns {number} Duration in seconds
 */
export const calculateDurationSeconds = (startTime, endTime) => {
  if (!startTime || !endTime) return 0;

  const start = typeof startTime === 'string' ? new Date(startTime) : startTime;
  const end = typeof endTime === 'string' ? new Date(endTime) : endTime;

  return differenceInSeconds(end, start);
};

// ==================== CALENDAR LAYOUT UTILITIES ====================

/**
 * Get the slot height in pixels based on view density
 * This must match the --calendar-slot-height CSS variable
 * @param {string} viewDensity - 'relaxed' or 'compact'
 * @returns {number} Slot height in pixels
 */
export const getSlotHeight = (viewDensity = 'relaxed') => {
  return viewDensity === 'relaxed' ? 20 : 16;
};

// ==================== EXPORT ALL ====================

export default {
  formatWorklogDate,
  formatDateRange,
  formatDateForBackend,
  formatDisplayDate,
  formatLongDate,
  formatMonthYear,
  formatTime12Hour,
  formatHourMinute12Hour,
  formatTime24Hour,
  formatTimeRange,
  formatDuration,
  formatDurationHuman,
  getWeekStart,
  getWeekEnd,
  getWeekDates,
  formatWeekRange,
  getDateRangeForPreset,
  isOvernightWorklog,
  isToday,
  isThisWeek,
  isThisMonth,
  isSameDay,
  timeStringToDate,
  createDateTimeInTimezone,
  getCurrentTimeFormatted,
  calculateDurationSeconds,
  getSlotHeight
};
